import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Checkbox, FormControlLabel, Grid, InputAdornment, Stack, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { Button } from "Components/_Shared/Buttons/Button";
import { DialogModal } from "Components/_Shared/DialogModal";
import { ReactComponent as AddIcon } from "Components/_Shared/Icons/Assets/add.svg";
import { ReactComponent as BinIcon } from "Components/_Shared/Icons/Assets/bin.svg";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { type HousingExpenseFormType, getHousingExpenseFormSchema } from "Forms/Schemas/Budget/Expenses/housingExpenseFormSchema";
import {
  getGetBudgetOverviewQueryKey,
  getGetHousingExpenseQueryKey,
  useGetHousingExpense,
  useReplaceHousingExpense,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { AmountFrequency, type HousingAmount, HousingAmountAmountType, HousingExpenseEntryMode } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { type RefObject, forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import React from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import type { ExpenseFormHandle } from "../Components/EditExpenseDrawer";

type Props = {
  onClose: () => void;
  ref?: RefObject<ExpenseFormHandle>;
};

export const HousingExpenseForm = forwardRef<ExpenseFormHandle, Props>(({ onClose }, ref) => {
  const { readOnly } = useContext(AnalysisDetailContext);
  const { t } = useResource();
  const theme = useTheme();
  const [detailed, setDetailed] = useState(true);
  const [openDeleteBulkModal, setOpenDeleteBulkModal] = useState<boolean>(false);
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const queryClient = useQueryClient();
  const { data: housingExpenseData } = useGetHousingExpense(analysisId, versionId);
  const { mutateAsync: replaceHousingExpenseAsync } = useReplaceHousingExpense();

  const {
    control,
    setValue,
    watch,
    formState: { errors },
    handleSubmit,
    clearErrors,
  } = useForm<HousingExpenseFormType>({
    resolver: yupResolver(getHousingExpenseFormSchema(t)),
    mode: "onBlur",
    defaultValues: {
      aggregatedAmount: (housingExpenseData?.aggregatedAmount?.value.toString() as any) || "",
      customItems: [],
      detailed: true,
    },
  });

  const {
    fields: customFields,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "customItems",
  });

  const customItems = watch("customItems");
  const rent = watch("rent");
  const electricity = watch("electricity");
  const gas = watch("gas");
  const water = watch("water");

  useEffect(() => {
    if (detailed) {
      const predefinedFieldsSum =
        (Number.parseFloat(rent ?? "0") || 0) +
        (Number.parseFloat(electricity ?? "0") || 0) +
        (Number.parseFloat(gas ?? "0") || 0) +
        (Number.parseFloat(water ?? "0") || 0);

      const customItemsSum =
        customItems?.reduce((sum, item) => {
          const value = Number.parseFloat(item.value ?? "0") || 0;
          return sum + value;
        }, 0) || 0;

      const total = predefinedFieldsSum + customItemsSum;
      setValue("aggregatedAmount", total.toString());
    }
  }, [customItems, rent, electricity, gas, water, detailed, setValue]);

  const recalculateTotal = () => {
    if (detailed) {
      const predefinedFieldsSum =
        (Number.parseFloat(rent ?? "0") || 0) +
        (Number.parseFloat(electricity ?? "0") || 0) +
        (Number.parseFloat(gas ?? "0") || 0) +
        (Number.parseFloat(water ?? "0") || 0);

      const customItemsSum =
        customItems?.reduce((sum, item) => {
          const value = Number.parseFloat(item.value ?? "0") || 0;
          return sum + value;
        }, 0) || 0;

      const total = predefinedFieldsSum + customItemsSum;
      setValue("aggregatedAmount", total.toString());
    }
  };

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(onSubmit)();
    },
    deleteExpense: () => {
      setOpenDeleteBulkModal(true);
    },
  }));

  useEffect(() => {
    if (housingExpenseData) {
      handleChangeDetailed(housingExpenseData.entryMode === HousingExpenseEntryMode.individual);
      setValue("aggregatedAmount", (housingExpenseData.aggregatedAmount?.value ?? 0).toString());

      const customItems: { name: string; value: string }[] = [];
      for (const expense of housingExpenseData.amounts) {
        switch (expense.amountType) {
          case HousingAmountAmountType.rent:
            setValue("rent", expense.amount.value.toString());
            break;
          case HousingAmountAmountType.electricity:
            setValue("electricity", expense.amount.value.toString());
            break;
          case HousingAmountAmountType.gas:
            setValue("gas", expense.amount.value.toString());
            break;
          case HousingAmountAmountType.water:
            setValue("water", expense.amount.value.toString());
            break;
          case HousingAmountAmountType.other:
            customItems.push({
              name: expense.name,
              value: expense.amount.value.toString(),
            });
            break;
        }
      }
      replace(customItems);
    }
  }, [housingExpenseData, setValue, replace]);

  const onSubmit = async (data: HousingExpenseFormType) => {
    if (Number(data.aggregatedAmount) === 0) {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseAmountZeroError));
      return;
    }

    const customItems: HousingAmount[] =
      data.customItems?.map((field) => ({
        amountType: HousingAmountAmountType.other,
        amount: {
          value: Number.parseFloat(field.value ?? "0") || 0,
          currencyCode: "CZK",
          frequency: AmountFrequency.monthly,
        },
        name: field.name ?? "",
      })) ?? [];

    const amounts: HousingAmount[] = detailed
      ? [
          {
            amountType: HousingAmountAmountType.rent,
            amount: {
              value: Number.parseFloat(data.rent ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.HousingExpenseTypes.Rent),
          },
          {
            amountType: HousingAmountAmountType.electricity,
            amount: {
              value: Number.parseFloat(data.electricity ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.HousingExpenseTypes.Electricity),
          },
          {
            amountType: HousingAmountAmountType.gas,
            amount: {
              value: Number.parseFloat(data.gas ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.HousingExpenseTypes.Gas),
          },
          {
            amountType: HousingAmountAmountType.water,
            amount: {
              value: Number.parseFloat(data.water ?? "0") || 0,
              currencyCode: "CZK",
              frequency: AmountFrequency.monthly,
            },
            name: t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.HousingExpenseTypes.Water),
          },
          ...customItems,
        ]
      : [];

    try {
      await replaceHousingExpenseAsync({
        analysisId,
        versionId,
        data: {
          entryMode: detailed ? HousingExpenseEntryMode.individual : HousingExpenseEntryMode.aggregate,
          aggregatedAmount: {
            value: Number.parseFloat(data.aggregatedAmount ?? "0") || 0,
            currencyCode: "CZK",
            frequency: AmountFrequency.monthly,
          },
          amounts,
        },
      });

      onClose();
    } catch {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
    } finally {
      queryClient.invalidateQueries({ queryKey: getGetHousingExpenseQueryKey(analysisId, versionId) });
      queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
    }
  };

  const onDelete = async () => {
    try {
      await replaceHousingExpenseAsync({
        analysisId,
        versionId,
        data: {
          entryMode: detailed ? HousingExpenseEntryMode.individual : HousingExpenseEntryMode.aggregate,
          aggregatedAmount: { value: 0, currencyCode: "CZK", frequency: AmountFrequency.monthly },
          amounts: [],
        },
      });

      queryClient.invalidateQueries({ queryKey: getGetHousingExpenseQueryKey(analysisId, versionId) });
      queryClient.invalidateQueries({ queryKey: getGetBudgetOverviewQueryKey(analysisId, versionId) });
      onClose();
    } catch {
      toast.error(t(Resources.AnalysisDetail.Budget.Expenses.Form.Errors.ExpenseSubmitError));
    }
  };

  const onDeleteBulkExpense = () => {
    replace([]);
    setOpenDeleteBulkModal(false);
    onDelete();
  };

  const handleChangeDetailed = (value: boolean) => {
    setDetailed(value);
    setValue("detailed", value);
    clearErrors();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={Spacing.XXL} pb={Spacing.XXL} sx={{ height: "100%" }}>
        <Box>
          <Typography fontWeight={600} variant="body1" mb={Spacing.XL}>
            {t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.Housing)}
          </Typography>

          <Grid container spacing={Spacing.XL}>
            <Grid size={6}>
              <BlFormInput
                control={control}
                name="aggregatedAmount"
                label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.Aggregated)}
                errors={errors}
                disabled={detailed || readOnly}
                inputEndAdornment={
                  <InputAdornment position="end">{`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}</InputAdornment>
                }
                mask={[
                  {
                    mask: Number,
                    thousandsSeparator: " ",
                    scale: 2,
                    min: 0,
                  },
                ]}
                placeholder="0"
              />
            </Grid>

            <Grid size={6}>
              <Box display="flex" alignItems="flex-start" mt={5} height="100%">
                <FormControlLabel
                  control={<Checkbox checked={detailed} disabled={readOnly} onChange={(_, checked) => handleChangeDetailed(checked)} />}
                  label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Fields.DetailedCheckbox)}
                />
              </Box>
            </Grid>

            {detailed && (
              <>
                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    name="rentName"
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.HousingExpenseTypes.Rent)}
                    label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.Item)}
                    disabled
                    errors={errors}
                  />
                </Grid>
                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    disabled={readOnly}
                    name="rent"
                    label={t(Resources.AnalysisDetail.Budget.Expenses.Form.Labels.Value)}
                    errors={errors}
                    inputEndAdornment={
                      <InputAdornment position="end">{`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}</InputAdornment>
                    }
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                        min: 0,
                      },
                    ]}
                    placeholder="0"
                    onChange={recalculateTotal}
                  />
                </Grid>

                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    name="electricityName"
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.HousingExpenseTypes.Electricity)}
                    disabled
                    errors={errors}
                  />
                </Grid>
                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    disabled={readOnly}
                    name="electricity"
                    errors={errors}
                    inputEndAdornment={
                      <InputAdornment position="end">{`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}</InputAdornment>
                    }
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                        min: 0,
                      },
                    ]}
                    placeholder="0"
                    onChange={recalculateTotal}
                  />
                </Grid>

                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    name="gasName"
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.HousingExpenseTypes.Gas)}
                    disabled
                    errors={errors}
                  />
                </Grid>
                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    disabled={readOnly}
                    name="gas"
                    errors={errors}
                    inputEndAdornment={
                      <InputAdornment position="end">{`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}</InputAdornment>
                    }
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                        min: 0,
                      },
                    ]}
                    placeholder="0"
                    onChange={recalculateTotal}
                  />
                </Grid>

                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    name="waterName"
                    value={t(Resources.AnalysisDetail.Budget.Expenses.ExpenseTypes.HousingExpenseTypes.Water)}
                    disabled
                    errors={errors}
                  />
                </Grid>
                <Grid size={6}>
                  <BlFormInput
                    control={control}
                    disabled={readOnly}
                    name="water"
                    errors={errors}
                    inputEndAdornment={
                      <InputAdornment position="end">{`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}</InputAdornment>
                    }
                    mask={[
                      {
                        mask: Number,
                        thousandsSeparator: " ",
                        scale: 2,
                        min: 0,
                      },
                    ]}
                    placeholder="0"
                    onChange={recalculateTotal}
                  />
                </Grid>

                {customFields.map((field, idx) => (
                  <React.Fragment key={field.id}>
                    <Grid size={6}>
                      <BlFormInput
                        control={control}
                        disabled={readOnly}
                        name={`customItems.${idx}.name`}
                        errors={errors}
                        placeholder={t(Resources.AnalysisDetail.Budget.Expenses.Form.Placehodlers.OwnName)}
                      />
                    </Grid>
                    <Grid size={5}>
                      <BlFormInput
                        control={control}
                        disabled={readOnly}
                        name={`customItems.${idx}.value`}
                        errors={errors}
                        inputEndAdornment={
                          <InputAdornment position="end">{`${t(Resources.Common.CZKSuffix)}/${t(Resources.Common.Month)}`}</InputAdornment>
                        }
                        mask={[
                          {
                            mask: Number,
                            thousandsSeparator: " ",
                            scale: 2,
                            min: 0,
                          },
                        ]}
                        placeholder="0"
                        onChange={recalculateTotal}
                      />
                    </Grid>
                    <Grid size={1} display="flex" alignItems="flex-start" justifyContent="center">
                      <Button disabled={readOnly} variant="text" onClick={() => remove(idx)}>
                        <BinIcon />
                      </Button>
                    </Grid>
                  </React.Fragment>
                ))}

                <Grid size={12}>
                  <Button
                    variant="text"
                    disabled={readOnly}
                    colorCustom={theme.colors.brandPrimary}
                    startAdornment={<AddIcon />}
                    onClick={() => append({ name: "", value: "" })}
                  >
                    {t(Resources.AnalysisDetail.Budget.Expenses.Form.Buttons.AddCustomItem)}
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Stack>

      <DialogModal
        isOpen={openDeleteBulkModal}
        onClose={() => setOpenDeleteBulkModal(false)}
        title={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpense)}
        subtitle={t(Resources.AnalysisDetail.Budget.Expenses.Form.DeleteExpenseHint)}
        primaryAction={
          <Button disabled={readOnly} onClick={onDeleteBulkExpense}>
            {t(Resources.Common.Yes)}
          </Button>
        }
      />
    </form>
  );
});
